import React from 'react';
import { SectionCard } from '../../common/SectionCard';
import { EmptyState } from '../../common/EmptyState';
import { formatDate } from '../../../utils/format';
import { cn } from '../../../utils/cn';
import type { ReadinessDetail } from '../../../types/domain';
const resultClasses = {
  passed: 'border-uae-leaf/30 bg-uae-leaf/10 text-uae-leaf',
  issues: 'border-uae-gold/40 bg-uae-gold/10 text-[#8a6f11]',
  scheduled: 'border-border bg-muted text-foreground'
};
export function ReadinessPanel({
  readiness
}: {
  readiness?: ReadinessDetail;
}) {
  if (!readiness) {
    return <EmptyState title="Readiness assessment not started" description="Readiness domains, test events and integrated assurance appear once the record clears the Plan & Build gate." data-id="element-715" data-mp="2bpvy1rc563s7" />;
  }
  const belowThreshold = readiness.domains.filter(d => d.score < d.threshold);
  return <div className="space-y-4" data-id="element-716" data-mp="1nzrrpc7kjpxi">
      <SectionCard title="Readiness domains" description="Each domain must reach its threshold before the gate can clear" actions={<span className={cn('rounded-sm border px-1.5 py-0.5 text-[11px]', belowThreshold.length === 0 ? 'border-uae-leaf/30 bg-uae-leaf/10 text-uae-leaf' : 'border-uae-red/30 bg-uae-red/10 text-uae-red')} data-id="element-718" data-mp="28zszjyttug5d">
            {belowThreshold.length === 0 ? 'All domains at threshold' : `${belowThreshold.length} below threshold`}
          </span>} bodyClassName="p-0" data-id="element-717" data-mp="1nyqi7c67bbhw">
        <ul className="divide-y divide-border" data-id="element-719" data-mp="1l9f5k2rom785">
          {readiness.domains.map(domain => {
          const below = domain.score < domain.threshold;
          return <li key={domain.id} className="px-4 py-3" data-id="element-720" data-mp="3fnzj7vhl52i1">
                <div className="flex items-center justify-between gap-3" data-id="element-721" data-mp="3fng22gzrei52">
                  <div className="min-w-0" data-id="element-722" data-mp="3aomvl8iw3ree">
                    <p className="truncate text-[13px] font-medium" data-id="element-723" data-mp="3amsbfwfg03bd">{domain.label}</p>
                    <p className="text-[11px] text-muted-foreground" data-id="element-724" data-mp="18yed0k2cn52z">{domain.owner}</p>
                  </div>
                  <div className="flex shrink-0 items-center gap-2" data-id="element-725" data-mp="1dsikr5jfvh54">
                    <span className={cn('font-mono text-[12px]', below ? 'text-uae-red' : 'text-foreground')} data-id="element-726" data-mp="z2uia0ziucft">
                      {domain.score}
                    </span>
                    <span className="font-mono text-[11px] text-muted-foreground" data-id="element-727" data-mp="1wo8qn3uhsxoa">
                      / {domain.threshold}
                    </span>
                  </div>
                </div>
                <div className="relative mt-1.5 h-2 w-full overflow-hidden rounded-full bg-muted" data-id="element-728" data-mp="1mq2p4nclltjy">
                  <div className={cn('h-full rounded-full', below ? 'bg-uae-red' : 'bg-uae-green')} style={{
                width: `${domain.score}%`
              }} data-id="element-729" data-mp="79akd3xqkofa" />
                  <span className="absolute top-0 h-full w-0.5 bg-uae-ink/50" style={{
                left: `${domain.threshold}%`
              }} aria-hidden="true" data-id="element-730" data-mp="3rejjsjpj70u" />
                </div>
              </li>;
        })}
        </ul>
      </SectionCard>

      <div className="grid gap-4 xl:grid-cols-[1.4fr_1fr]" data-id="element-731" data-mp="8su3tie299i6">
        <SectionCard title="Test events & rehearsals" bodyClassName="p-0" data-id="element-732" data-mp="3h72o836fitz9">
          <ul className="divide-y divide-border" data-id="element-733" data-mp="3h4zogrfh5mhc">
            {readiness.tests.map(test => <li key={test.id} className="flex items-center justify-between gap-3 px-4 py-2.5" data-id="element-734" data-mp="3c6eqkynp3ew2">
                <div className="min-w-0" data-id="element-735" data-mp="3qyyrdsndhaac">
                  <p className="truncate text-[13px]" data-id="element-736" data-mp="3m65o797jya0d">{test.label}</p>
                  <p className="text-[11px] text-muted-foreground" data-id="element-737" data-mp="2yguozw5uonoc">{formatDate(test.on)}</p>
                </div>
                <span className={cn('shrink-0 rounded-sm border px-1.5 py-0.5 text-[11px] capitalize', resultClasses[test.result])} data-id="element-738" data-mp="2ti9qq2a5se2j">
                  {test.result}
                </span>
              </li>)}
          </ul>
        </SectionCard>

        <SectionCard title="Integrated assurance" data-id="element-739" data-mp="25pqdacz4ddsa">
          <p className="text-xl font-semibold" data-id="element-740" data-mp="2aib02ujlyqmb">
            {readiness.assuranceSignedOff ? 'Signed off' : 'Held'}
          </p>
          <p className="mt-1.5 text-[12px] leading-relaxed text-muted-foreground" data-id="element-741" data-mp="2fg2x22z412xd">
            {readiness.assuranceSignedOff ? 'The independent assurance panel has signed off operational readiness across all domains.' : `The independent assurance panel is holding sign-off${belowThreshold.length > 0 ? ` until ${belowThreshold.map(d => d.label.toLowerCase()).join(' and ')} reach threshold.` : '.'}`}
          </p>
        </SectionCard>
      </div>
    </div>;
}